import React, { useState, useEffect } from 'react';
import { Modal, Table, Tag, Space, Typography, Spin, Card, Divider } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { inventoryAPI } from '../services/api.ts';

const { Text, Title } = Typography;

interface AvailabilityData {
  gross: number;
  reserved: number;
  net: number;
}

interface DateGroupItem {
  expected_date: string | null;
  gross: number;
  reserved: number;
  net: number;
}

interface AvailabilitySummary {
  product_id: number;
  product_code: string;
  product_description: string;
  available: AvailabilityData;
  in_transit: DateGroupItem[];
  in_production: DateGroupItem[];
  total_unreserved: number;
}

interface ProductInventoryInfoModalProps {
  visible: boolean;
  productId: number | null;
  productName: string;
  onClose: () => void;
}

const ProductInventoryInfoModal: React.FC<ProductInventoryInfoModalProps> = ({
  visible,
  productId,
  productName,
  onClose
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<AvailabilitySummary | null>(null);

  useEffect(() => {
    if (visible && productId) {
      loadData(productId);
    }
    if (!visible) {
      setData(null);
    }
  }, [visible, productId]);

  const loadData = async (pid: number) => {
    setLoading(true);
    try {
      const result = await inventoryAPI.getProductAvailabilitySummary(pid);
      setData(result);
    } catch (error) {
      console.error('Errore caricamento disponibilità prodotto:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Data non disponibile';
    try {
      return new Date(dateStr).toLocaleDateString('it-IT');
    } catch {
      return dateStr;
    }
  };

  const dateGroupColumns = (dateLabel: string) => [
    {
      title: dateLabel,
      dataIndex: 'expected_date',
      key: 'expected_date',
      width: 180,
      render: (val: string | null) => (
        <Text>{formatDate(val)}</Text>
      ),
    },
    {
      title: 'Lordo',
      dataIndex: 'gross',
      key: 'gross',
      width: 100,
      align: 'center' as const,
      render: (val: number) => <Text>{val}</Text>,
    },
    {
      title: 'Prenotato',
      dataIndex: 'reserved',
      key: 'reserved',
      width: 100,
      align: 'center' as const,
      render: (val: number) => (
        <Text type={val > 0 ? 'danger' : undefined}>{val}</Text>
      ),
    },
    {
      title: 'Netto',
      dataIndex: 'net',
      key: 'net',
      width: 100,
      align: 'center' as const,
      render: (val: number) => (
        <Text strong style={{ color: val > 0 ? '#52c41a' : val < 0 ? '#ff4d4f' : undefined }}>
          {val}
        </Text>
      ),
    },
  ];

  const getTotalRow = (items: DateGroupItem[]) => {
    if (items.length === 0) return null;
    return {
      gross: items.reduce((sum, i) => sum + i.gross, 0),
      reserved: items.reduce((sum, i) => sum + i.reserved, 0),
      net: items.reduce((sum, i) => sum + i.net, 0),
    };
  };

  return (
    <Modal
      title={
        <Space>
          <InfoCircleOutlined />
          <span>Info Inventario Prodotto</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={700}
    >
      <Spin spinning={loading}>
        {data && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            {/* Nome Prodotto */}
            <div style={{ marginBottom: 4 }}>
              <Text type="secondary" style={{ fontSize: 13 }}>{productName}</Text>
            </div>

            {/* SEZIONE DISPONIBILE */}
            <Card
              size="small"
              title={<Text strong style={{ color: '#52c41a' }}>Disponibile</Text>}
            >
              <div style={{ display: 'flex', justifyContent: 'space-around', textAlign: 'center' }}>
                <div>
                  <div style={{ fontSize: 12, color: '#999' }}>Lordo</div>
                  <div style={{ fontSize: 20, fontWeight: 'bold' }}>{data.available.gross}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#999' }}>Prenotato</div>
                  <div style={{ fontSize: 20, fontWeight: 'bold', color: data.available.reserved > 0 ? '#ff4d4f' : undefined }}>
                    {data.available.reserved}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: '#999' }}>Netto</div>
                  <div style={{ fontSize: 20, fontWeight: 'bold', color: data.available.net > 0 ? '#52c41a' : data.available.net < 0 ? '#ff4d4f' : undefined }}>
                    {data.available.net}
                  </div>
                </div>
              </div>
            </Card>

            {/* SEZIONE IN TRANSITO */}
            <Card
              size="small"
              title={<Text strong style={{ color: '#fa8c16' }}>In Transito</Text>}
            >
              {data.in_transit.length > 0 ? (
                <>
                  <Table
                    dataSource={data.in_transit}
                    columns={dateGroupColumns('Data Arrivo Prevista')}
                    rowKey={(_, index) => `transit-${index}`}
                    pagination={false}
                    size="small"
                    bordered
                  />
                  {data.in_transit.length > 1 && (() => {
                    const totals = getTotalRow(data.in_transit);
                    return totals && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, marginTop: 8 }}>
                        <Text type="secondary">Totale:</Text>
                        <Text>Lordo: <strong>{totals.gross}</strong></Text>
                        <Text>Prenotato: <strong>{totals.reserved}</strong></Text>
                        <Text>Netto: <Text strong style={{ color: totals.net > 0 ? '#52c41a' : undefined }}>{totals.net}</Text></Text>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <Text type="secondary" style={{ fontStyle: 'italic' }}>Nessun prodotto in transito</Text>
              )}
            </Card>

            {/* SEZIONE IN PRODUZIONE */}
            <Card
              size="small"
              title={<Text strong style={{ color: '#1890ff' }}>In Produzione</Text>}
            >
              {data.in_production.length > 0 ? (
                <>
                  <Table
                    dataSource={data.in_production}
                    columns={dateGroupColumns('Data Consegna Stimata')}
                    rowKey={(_, index) => `prod-${index}`}
                    pagination={false}
                    size="small"
                    bordered
                  />
                  {data.in_production.length > 1 && (() => {
                    const totals = getTotalRow(data.in_production);
                    return totals && (
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, marginTop: 8 }}>
                        <Text type="secondary">Totale:</Text>
                        <Text>Lordo: <strong>{totals.gross}</strong></Text>
                        <Text>Prenotato: <strong>{totals.reserved}</strong></Text>
                        <Text>Netto: <Text strong style={{ color: totals.net > 0 ? '#52c41a' : undefined }}>{totals.net}</Text></Text>
                      </div>
                    );
                  })()}
                </>
              ) : (
                <Text type="secondary" style={{ fontStyle: 'italic' }}>Nessun prodotto in produzione</Text>
              )}
            </Card>

            {/* TOTALE NON RISERVATO */}
            <Divider style={{ margin: '8px 0' }} />
            <div style={{ textAlign: 'center' }}>
              <Tag
                color={data.total_unreserved > 0 ? 'green' : data.total_unreserved === 0 ? 'default' : 'red'}
                style={{ fontSize: 16, padding: '8px 24px' }}
              >
                Totale Non Riservato: <strong>{data.total_unreserved}</strong>
              </Tag>
            </div>
          </Space>
        )}
      </Spin>
    </Modal>
  );
};

export default ProductInventoryInfoModal;
